import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import type { PhotoComment } from "@tih/shared";
import { useAuth } from "../context/AuthContext";
import {
  ApiError,
  deletePhotoComment,
  fetchPhotoComments,
  postPhotoComment,
} from "../api/client";

const POLL_MS = 8000;
const MAX_LEN = 1000;

function formatTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

type Props = {
  photoId: number;
  compact?: boolean;
  initialComments?: PhotoComment[];
};

export function PhotoComments({ photoId, compact = false, initialComments }: Props) {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [comments, setComments] = useState<PhotoComment[]>(initialComments ?? []);
  const [loading, setLoading] = useState(initialComments === undefined);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingNote, setPendingNote] = useState<string | null>(null);

  useEffect(() => {
    setComments(initialComments ?? []);
    setLoading(initialComments === undefined);
  }, [photoId, initialComments]);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function poll() {
      try {
        const next = await fetchPhotoComments(photoId);
        if (!cancelled) {
          setComments(next);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      } finally {
        if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
      }
    }

    timer = window.setTimeout(
      () => void poll(),
      initialComments !== undefined ? POLL_MS : 0,
    );

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [photoId, initialComments]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    setPendingNote(null);
    try {
      await postPhotoComment(photoId, { body: trimmed });
      setBody("");
      setPendingNote(
        "Thank you — your comment will appear after a moderator approves it.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not post your comment");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: number) {
    const before = comments;
    setComments((cs) => cs.filter((c) => c.id !== id));
    try {
      await deletePhotoComment(photoId, id);
    } catch {
      setComments(before);
    }
  }

  const rootClass = compact ? "photo-comments photo-comments-compact" : "photo-comments";

  return (
    <section className={rootClass} aria-label="Photo comments">
      <header className="photo-comments-head">
        <h2>
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h2>
      </header>

      {user ? (
        <form className="photo-comment-form" onSubmit={onSubmit}>
          <div className="lc-avatar" aria-hidden="true">{initials(user.name)}</div>
          <div className="lc-form-body">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={MAX_LEN}
              rows={compact ? 2 : 3}
              placeholder="Write a comment…"
              disabled={busy}
            />
            <div className="lc-form-row">
              <span className="lc-count">{body.length}/{MAX_LEN}</span>
              <div className="lc-form-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy || !body}
                  onClick={() => setBody("")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={busy || !body.trim()}
                >
                  {busy ? "Posting…" : "Comment"}
                </button>
              </div>
            </div>
            {error && <div className="form-error">{error}</div>}
            {pendingNote && (
              <div className="form-success" style={{ padding: 10 }}>
                {pendingNote}
              </div>
            )}
          </div>
        </form>
      ) : (
        <div className="photo-comment-signin">
          <Link
            to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`}
            className="btn btn-primary"
          >
            Sign in to comment
          </Link>
        </div>
      )}

      {loading ? (
        <div className="photo-comments-empty">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="photo-comments-empty">No comments yet. Be the first.</div>
      ) : (
        <ul className="photo-comment-list">
          {comments.map((c) => {
            const canDelete =
              !!user && (isAdmin || (c.authorId != null && c.authorId === user.id));
            return (
              <li className="photo-comment" key={c.id}>
                <div className="lc-avatar" aria-hidden="true">
                  {initials(c.authorName)}
                </div>
                <div className="lc-body">
                  <div className="lc-meta">
                    <span className="lc-author">{c.authorName}</span>
                    <span className="lc-time">{formatTime(c.createdAt)}</span>
                    {canDelete && (
                      <button
                        type="button"
                        className="lc-delete"
                        onClick={() => onDelete(c.id)}
                        aria-label="Delete comment"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="lc-text">{c.body}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
